var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/crypto.ts
import { G, S } from "@mobily/ts-belt";
import { customAlphabet } from "nanoid";
var CHAR_SETS = {
  ALPHA: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz",
  NUMBERS: "0123456789",
  URL_SAFE: "-_",
  BASE64_EXTRA: "+/",
  HEX: "0123456789abcdef",
  HEX_UPPER: "0123456789ABCDEF"
};
function getChars(...types) {
  let result = "";
  for (const type of types) {
    if (type in CHAR_SETS) {
      result += CHAR_SETS[type];
    } else {
      result += type;
    }
  }
  return result;
}
__name(getChars, "getChars");
var COMMON_CHAR_SETS = {
  HEX_LOWER: CHAR_SETS.HEX,
  HEX_UPPER: CHAR_SETS.HEX_UPPER,
  ALPHANUMERIC: getChars("ALPHA", "NUMBERS"),
  BASE64: getChars("ALPHA", "NUMBERS", "BASE64_EXTRA"),
  URL_SAFE_ALPHANUMERIC: getChars("ALPHA", "NUMBERS", "URL_SAFE")
};
function hasCrypto(funcName) {
  if (typeof crypto === "undefined" || G.isNullable(crypto)) {
    return false;
  }
  if (G.isNullable(funcName)) {
    return true;
  }
  if (funcName === "subtle") {
    return G.isNotNullable(crypto.subtle);
  }
  return G.isNotNullable(crypto[funcName]);
}
__name(hasCrypto, "hasCrypto");
function toArrayBuffer(data) {
  if (typeof data === "string") {
    const encoder = new TextEncoder();
    const encoded = encoder.encode(data);
    return encoded.buffer.slice(
      encoded.byteOffset,
      encoded.byteOffset + encoded.byteLength
    );
  }
  if (data instanceof Uint8Array) {
    return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
  }
  return data;
}
__name(toArrayBuffer, "toArrayBuffer");
var nanoid = customAlphabet(COMMON_CHAR_SETS.ALPHANUMERIC, 12);
function generateNanoId(length = 12, alphabet = COMMON_CHAR_SETS.ALPHANUMERIC) {
  return customAlphabet(alphabet, length)();
}
__name(generateNanoId, "generateNanoId");
function generateUrlSafeNanoId(length = 12) {
  return customAlphabet(COMMON_CHAR_SETS.URL_SAFE_ALPHANUMERIC, length)();
}
__name(generateUrlSafeNanoId, "generateUrlSafeNanoId");
function generatePrefixedId(prefix, options = {}) {
  const { type = "nano", length = 12 } = options;
  if (type === "uuid") {
    return `${prefix}_${generateUUID()}`;
  }
  return `${prefix}_${generateNanoId(length)}`;
}
__name(generatePrefixedId, "generatePrefixedId");
var isNanoId = /* @__PURE__ */ __name((id, length = 12) => new RegExp(`^[0-9A-Za\
-z]{${length}}$`).test(id), "isNanoId");
function simpleId() {
  let count = 0;
  return {
    next() {
      count = (count + 1) % Number.MAX_SAFE_INTEGER;
      return count.toString();
    }
  };
}
__name(simpleId, "simpleId");
function generateId(length = 12, charSet = COMMON_CHAR_SETS.ALPHANUMERIC) {
  let result = "";
  if (hasCrypto("getRandomValues")) {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      const byte = array[i] ?? 0;
      result += charSet[byte % charSet.length];
    }
  } else {
    for (let i = 0; i < length; i++) {
      result += charSet.charAt(Math.floor(Math.random() * charSet.length));
    }
  }
  return result;
}
__name(generateId, "generateId");
function generateUUID() {
  if (hasCrypto("randomUUID")) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : r & 3 | 8;
    return v.toString(16);
  });
}
__name(generateUUID, "generateUUID");
function simpleHash(str) {
  let hash = 0;
  if (S.isEmpty(str)) {
    return hash;
  }
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash &= hash;
  }
  return Math.abs(hash);
}
__name(simpleHash, "simpleHash");
function randomHexColor() {
  if (hasCrypto("getRandomValues")) {
    const array = new Uint8Array(3);
    crypto.getRandomValues(array);
    return `#${Array.from(array).map((b) => b.toString(16).padStart(2, "0")).join(
    "")}`;
  }
  return `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0")}`;
}
__name(randomHexColor, "randomHexColor");
function toBase64(str) {
  if (typeof btoa !== "undefined") {
    return btoa(unescape(encodeURIComponent(str)));
  }
  const bytes = new TextEncoder().encode(str);
  const base64chars = COMMON_CHAR_SETS.BASE64;
  let result = "";
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i] ?? 0;
    const b = bytes[i + 1] ?? 0;
    const c = bytes[i + 2] ?? 0;
    const chunk = a << 16 | b << 8 | c;
    result += base64chars[chunk >> 18 & 63];
    result += base64chars[chunk >> 12 & 63];
    result += i + 1 < bytes.length ? base64chars[chunk >> 6 & 63] : "=";
    result += i + 2 < bytes.length ? base64chars[chunk & 63] : "=";
  }
  return result;
}
__name(toBase64, "toBase64");
function fromBase64(base64) {
  if (typeof atob !== "undefined") {
    return decodeURIComponent(escape(atob(base64)));
  }
  const base64chars = COMMON_CHAR_SETS.BASE64;
  const bytes = [];
  for (let i = 0; i < base64.length; i += 4) {
    const a = base64chars.indexOf(base64[i] ?? "") || 0;
    const b = base64chars.indexOf(base64[i + 1] ?? "") || 0;
    const c = base64chars.indexOf(base64[i + 2] ?? "") || 0;
    const d = base64chars.indexOf(base64[i + 3] ?? "") || 0;
    const chunk = a << 18 | b << 12 | c << 6 | d;
    bytes.push(chunk >> 16 & 255);
    if (base64[i + 2] !== "=") {
      bytes.push(chunk >> 8 & 255);
    }
    if (base64[i + 3] !== "=") {
      bytes.push(chunk & 255);
    }
  }
  return new TextDecoder().decode(new Uint8Array(bytes));
}
__name(fromBase64, "fromBase64");
function randomInt(min, max) {
  if (hasCrypto("getRandomValues")) {
    const range = max - min + 1;
    const array = new Uint32Array(1);
    let value;
    do {
      crypto.getRandomValues(array);
      value = array[0] ?? 0;
    } while (value >= Math.floor(2 ** 32 / range) * range);
    return min + value % range;
  }
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
__name(randomInt, "randomInt");
function shuffle(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = randomInt(0, i);
    const a = shuffled[i];
    const b = shuffled[j];
    if (a !== void 0 && b !== void 0) {
      shuffled[i] = b;
      shuffled[j] = a;
    }
  }
  return shuffled;
}
__name(shuffle, "shuffle");
function randomBytes(size) {
  const buffer = new Uint8Array(size);
  if (hasCrypto("getRandomValues")) {
    crypto.getRandomValues(buffer);
  } else {
    for (let i = 0; i < size; i++) {
      buffer[i] = Math.floor(Math.random() * 256);
    }
  }
  return buffer;
}
__name(randomBytes, "randomBytes");
function randomToken(length = 32, charSet = COMMON_CHAR_SETS.URL_SAFE_ALPHANUMERIC) {
  let token = "";
  if (hasCrypto("getRandomValues")) {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      const byte = array[i] ?? 0;
      token += charSet[byte % charSet.length];
    }
  } else {
    for (let i = 0; i < length; i++) {
      token += charSet.charAt(Math.floor(Math.random() * charSet.length));
    }
  }
  return token;
}
__name(randomToken, "randomToken");
async function sha256(data) {
  if (hasCrypto("subtle")) {
    const arrayBuffer = toArrayBuffer(data);
    const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  throw new Error("Web Crypto API not available");
}
__name(sha256, "sha256");
async function sha512(data) {
  if (hasCrypto("subtle")) {
    const arrayBuffer = toArrayBuffer(data);
    const hashBuffer = await crypto.subtle.digest("SHA-512", arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  throw new Error("Web Crypto API not available");
}
__name(sha512, "sha512");
async function hmacSha256(key, data) {
  if (hasCrypto("subtle")) {
    const keyBuffer = toArrayBuffer(key);
    const dataBuffer = toArrayBuffer(data);
    const cryptoKey = await crypto.subtle.importKey(
      "raw",
      keyBuffer,
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"]
    );
    const signature = await crypto.subtle.sign("HMAC", cryptoKey, dataBuffer);
    const hashArray = Array.from(new Uint8Array(signature));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  throw new Error("Web Crypto API not available");
}
__name(hmacSha256, "hmacSha256");
function timingSafeEqual(a, b) {
  const aArray = typeof a === "string" ? new TextEncoder().encode(a) : a;
  const bArray = typeof b === "string" ? new TextEncoder().encode(b) : b;
  if (aArray.length !== bArray.length) {
    return false;
  }
  let result = 0;
  for (let i = 0; i < aArray.length; i++) {
    const aVal = aArray[i];
    const bVal = bArray[i];
    if (G.isNotNullable(aVal) && G.isNotNullable(bVal)) {
      result |= aVal ^ bVal;
    }
  }
  return result === 0;
}
__name(timingSafeEqual, "timingSafeEqual");
export {
  CHAR_SETS,
  COMMON_CHAR_SETS,
  fromBase64,
  generateId,
  generateNanoId,
  generatePrefixedId,
  generateUUID,
  generateUrlSafeNanoId,
  getChars,
  hasCrypto,
  hmacSha256,
  isNanoId,
  nanoid,
  randomBytes,
  randomHexColor,
  randomInt,
  randomToken,
  sha256,
  sha512,
  shuffle,
  simpleHash,
  simpleId,
  timingSafeEqual,
  toBase64
};
